import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { Loader2, Sparkles } from "lucide-react";
import { api } from "@/lib/api/client";
import { Button } from "@/components/ui/button";

type SummaryResult = { content: string; cached: boolean };

/**
 * Nút "Tóm tắt" — TỐN TIỀN THẬT mỗi lần gọi (trừ khi đã có bản lưu sẵn), nên:
 * - Chỉ gọi khi người dùng bấm, không tự động.
 * - Có trạng thái đang chạy rõ ràng, khoá nút trong lúc chờ.
 * - HTTP 429 (vượt hạn mức chi tiêu) phải hiện đúng nguyên văn thông báo tiếng
 *   Việt máy chủ trả về (đọc ở err.response.data.msg — theo AllExceptionsFilter
 *   của API), không thay bằng lỗi chung chung.
 */
export function SummaryButton({ attachId, textId }: { attachId: number; textId?: number | null }) {
  const [result, setResult] = useState<SummaryResult | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(`/ai/summary/${attachId}`, { textId: textId ?? undefined });
      return (res.data?.data ?? res.data) as SummaryResult;
    },
    onMutate: () => {
      setErrorMsg(null);
    },
    onSuccess: (data) => {
      setResult(data);
    },
    onError: (err) => {
      const ax = err as AxiosError<{ msg?: string }>;
      // 429: thông báo hạn mức đã soạn sẵn ở BudgetExceededError, đọc thẳng từ
      // máy chủ chứ không tự bịa chữ — đúng yêu cầu "không phải lỗi chung chung".
      setResult(null);
      setErrorMsg(
        ax.response?.data?.msg ??
          (ax.response?.status === 429
            ? "Đã vượt hạn mức chi tiêu AI."
            : "Không tóm tắt được, vui lòng thử lại sau."),
      );
    },
  });

  return (
    <div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={mutation.isPending || !Number.isFinite(attachId) || attachId <= 0}
        onClick={() => mutation.mutate()}
      >
        {mutation.isPending ? (
          <>
            <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Đang tóm tắt…
          </>
        ) : (
          <>
            <Sparkles className="h-4 w-4 mr-1.5" /> Tóm tắt (AI)
          </>
        )}
      </Button>

      {errorMsg && (
        <div className="mt-2 text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-md px-3 py-2">
          {errorMsg}
        </div>
      )}

      {result && (
        <div className="mt-2 rounded-md border border-border bg-muted/30 px-3 py-2.5 text-sm">
          <div className="whitespace-pre-wrap text-foreground">{result.content}</div>
          {result.cached && (
            <div className="mt-1.5 text-xs text-muted-foreground">
              Đã lưu — dùng lại bản tóm tắt trước đó, không tốn thêm chi phí.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
